import React from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Zoom,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";
import BackgroundWrapper from "../BackgroundWrapper";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import GroupIcon from "@mui/icons-material/Group";
import NotificationsIcon from "@mui/icons-material/Notifications";

const HomeClosed: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <BackgroundWrapper>
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: 900,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Zoom in>
          <Box sx={{ textAlign: "center", mb: 4, px: 1 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              gutterBottom
              sx={{
                color: theme.palette.primary.main,
                fontSize: { xs: "1.8rem", sm: "2.2rem" },
              }}
            >
              Tournoi National SQY Ping 2025
            </Typography>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem" } }}
            >
              28 et 29 juin - Gymnase Alain Mimoun, Villepreux (78)
            </Typography>
          </Box>
        </Zoom>

        <Zoom in>
          <Box
            sx={{
              backgroundColor: theme.palette.warning.light,
              borderLeft: `6px solid ${theme.palette.warning.main}`,
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              mb: 4,
              textAlign: "center",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={600}
              color="warning.dark"
              gutterBottom
            >
              🚫 Inscriptions fermées
            </Typography>
            <Typography variant="body1" color="warning.dark">
              Les inscriptions en ligne sont maintenant closes. Le tournoi
              commence bientôt !
            </Typography>
          </Box>
        </Zoom>

        <Zoom in>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              mb: 4,
              flexWrap: "wrap",
            }}
          >
            <Button
              component={Link}
              to="/live-player"
              variant="contained"
              color="primary"
              size="large"
              startIcon={<GroupIcon />}
              sx={{
                fontWeight: 600,
                px: 4,
                width: { xs: "100%", sm: "auto" },
                backgroundColor: theme.palette.success.main,
                "&:hover": {
                  backgroundColor: theme.palette.success.dark,
                },
              }}
            >
              Joueurs en match
            </Button>

            {!isMobile && (
              <Button
                component={Link}
                to="/live"
                variant="contained"
                color="secondary"
                size="large"
                startIcon={<LiveTvIcon />}
                sx={{
                  fontWeight: 600,
                  px: 4,
                  width: { xs: "100%", sm: "auto" },
                  backgroundColor: theme.palette.info.main,
                  "&:hover": {
                    backgroundColor: theme.palette.info.dark,
                  },
                }}
              >
                Tables en direct
              </Button>
            )}
          </Box>
        </Zoom>

        <Box
          sx={{
            backgroundColor: theme.palette.primary.light,
            borderLeft: `6px solid ${theme.palette.primary.main}`,
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            mb: 4,
          }}
        >
          <Typography variant="h6" fontWeight={600} gutterBottom>
            🎾 Le tournoi est en cours !
          </Typography>
          <Typography paragraph>
            Suivez en temps réel quels joueurs sont actuellement en match et sur
            quelles tables ils se trouvent !
          </Typography>
          <Typography variant="body2">
            Découvrez qui joue actuellement depuis notre plateforme.
          </Typography>
        </Box>

        <Zoom in>
          <Box
            sx={{
              backgroundColor: theme.palette.info.light,
              borderLeft: `6px solid ${theme.palette.info.main}`,
              p: { xs: 2, sm: 3 },
              borderRadius: 2,
              mb: 4,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <NotificationsIcon
                sx={{
                  mr: 1,
                  color: theme.palette.info.main,
                  fontSize: "1.5rem",
                }}
              />
              <Typography variant="h6" fontWeight={600} color="info.dark">
                🔔 Restez informé !
              </Typography>
            </Box>
            <Typography paragraph color="info.dark">
              Activez les notifications pour être alerté en temps réel des
              changements de statut des matchs de vos joueurs favoris !
            </Typography>
            <Typography variant="body2" color="info.dark" sx={{ mb: 2 }}>
              Recevez des alertes instantanées quand :
            </Typography>
            <ul
              style={{
                margin: "0 0 16px 0",
                paddingLeft: "20px",
                color: theme.palette.info.dark,
              }}
            >
              <li>Un match est créé</li>
              <li>Un match commence</li>
              <li>Un match est bloqué ou débloqué</li>
            </ul>
            <Button
              component={Link}
              to="/player-follow"
              variant="contained"
              color="info"
              size="large"
              startIcon={<NotificationsIcon />}
              sx={{
                fontWeight: 600,
                px: 4,
                backgroundColor: theme.palette.info.main,
                "&:hover": {
                  backgroundColor: theme.palette.info.dark,
                },
              }}
            >
              Configurer mes notifications
            </Button>
          </Box>
        </Zoom>

        <Box sx={{ flexGrow: 1 }}>
          <Box
            sx={{
              mt: { xs: 4, md: 8 },
              p: { xs: 2, sm: 3 },
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Comment suivre les matchs ?
            </Typography>
            <ul style={{ marginTop: 0 }}>
              <li>
                <strong>Joueurs en match :</strong> Consultez la liste des
                joueurs actuellement en match avec leur table, heure de début et
                durée
              </li>
              <li>
                <strong>Tables en direct :</strong> Visualisez l'état de toutes
                les tables avec les joueurs actuellement en match
              </li>
              <li>
                <strong>Mise à jour automatique :</strong> Les données se
                mettent à jour en temps réel
              </li>
            </ul>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Le tournoi se déroule sur 36 tables avec une buvette sur place.
              Venez encourager les participants !
            </Typography>
          </Box>
        </Box>
      </Box>
    </BackgroundWrapper>
  );
};

export default HomeClosed;
